import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { platformBilling, platformPlans, platformPacks, platformUpdatePlan, platformUpdatePack, platformStripeSync, platformTrials } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { RequirePlatform } from "./guards";
import { Shell, Card, H1, Btn, Label, Input } from "./ui";

const money = (n) => `£${Number(n || 0).toFixed(2)}`;
const StatusPill = ({ s, unlimited }) => {
  const map = { active: "text-[#00D26A]", trialing: "text-blue-400", past_due: "text-yellow-400", canceled: "text-red-400", none: "text-white/40" };
  return <span className={`text-xs font-semibold ${unlimited ? "text-[#00D26A]" : (map[s] || "text-white/50")}`}>{unlimited ? "unlimited" : (s || "—")}</span>;
};

function TrialStats() {
  const [d, setD] = useState(null);
  useEffect(() => { platformTrials().then(setD).catch(() => {}); }, []);
  if (!d) return null;
  const cards = [
    ["Total trials", d.total_trials], ["Active trials", d.active_trials], ["Converted", d.converted],
    ["Conversion %", `${d.conversion_pct}%`], ["Avg days to convert", d.avg_days_to_convert ?? "—"], ["Churned", d.churned],
  ];
  return (
    <Card data-testid="trial-stats">
      <h2 className="font-semibold mb-4">Trial conversion</h2>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {cards.map(([l, v]) => (
          <div key={l} data-testid={`trial-${l.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
            <div className="text-2xl font-bold">{v}</div>
            <div className="text-white/40 text-xs uppercase mt-1">{l}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TenantTable() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  useEffect(() => { platformBilling().then((d) => setRows(d.tenants || [])).catch(() => {}); }, []);
  const shown = rows.filter((r) =>
    (filter === "all" || r.status === filter || (filter === "unlimited" && r.unlimited)) &&
    (!q || r.name.toLowerCase().includes(q.toLowerCase()) || r.slug.includes(q.toLowerCase())));
  return (
    <Card>
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <h2 className="font-semibold">Tenant subscriptions ({rows.length})</h2>
        <div className="flex gap-2">
          <Input placeholder="Search tenants…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="billing-search" className="w-44" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="billing-filter" className="bg-[#0a0a0a] border border-white/15 rounded px-3 py-2 text-sm">
            <option value="all">All</option><option value="active">Active</option><option value="trialing">Trialing</option>
            <option value="past_due">Past due</option><option value="canceled">Cancelled</option><option value="unlimited">Unlimited</option>
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="platform-billing-table">
          <thead><tr className="text-white/40 text-xs uppercase text-left border-b border-white/10">
            <th className="py-2 pr-3">Tenant</th><th className="pr-3">Plan</th><th className="pr-3">Status</th>
            <th className="pr-3">AI allowance</th><th className="pr-3">Used</th><th className="pr-3">Bonus</th>
            <th className="pr-3">Remaining</th><th className="pr-3">Renews</th>
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.slug} className="border-b border-white/5" data-testid={`billing-row-${r.slug}`}>
                <td className="py-2 pr-3"><div className="font-semibold">{r.name}</div><div className="text-xs text-white/40">{r.slug}</div></td>
                <td className="pr-3 capitalize">{r.plan_name || r.plan_id || "—"}</td>
                <td className="pr-3"><StatusPill s={r.status} unlimited={r.unlimited} /></td>
                <td className="pr-3">{r.unlimited ? "∞" : r.ai_limit}</td>
                <td className="pr-3">{r.unlimited ? "—" : r.ai_used}</td>
                <td className="pr-3">{r.ai_bonus || 0}</td>
                <td className="pr-3">{r.unlimited ? "∞" : r.ai_remaining}</td>
                <td className="pr-3 text-white/50 text-xs">{(r.renewal_date || "").slice(0, 10) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PlanEditor() {
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const load = () => { platformPlans().then(setPlans).catch(() => {}); platformPacks().then(setPacks).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const setPlan = (i, k, v) => setPlans((s) => s.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const setPack = (i, k, v) => setPacks((s) => s.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const savePlan = async (p) => { try { await platformUpdatePlan(p.plan_id, { name: p.name, price_gbp: Number(p.price_gbp), price_gbp_yearly: Number(p.price_gbp_yearly), ai_allowance: Number(p.ai_allowance), active: p.active, trial_available: p.trial_available }); toast.success(`${p.name} saved`); } catch (e) { toast.error("Could not save plan"); } };
  const savePack = async (p) => { try { await platformUpdatePack(p.pack_id, { name: p.name, price_gbp: Number(p.price_gbp), credits: Number(p.credits), active: p.active }); toast.success(`${p.name} saved`); } catch (e) { toast.error("Could not save pack"); } };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <h2 className="font-semibold mb-4">Subscription plans</h2>
        <div className="space-y-3" data-testid="plan-editor">
          {plans.map((p, i) => (
            <div key={p.plan_id} className="bg-[#0a0a0a] border border-white/10 rounded p-3" data-testid={`plan-${p.plan_id}`}>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Name</Label><Input value={p.name} onChange={(e) => setPlan(i, "name", e.target.value)} data-testid={`plan-name-${p.plan_id}`} /></div>
                <div><Label>Price £/mo</Label><Input type="number" step="0.01" value={p.price_gbp} onChange={(e) => setPlan(i, "price_gbp", e.target.value)} data-testid={`plan-price-${p.plan_id}`} /></div>
                <div><Label>Price £/yr</Label><Input type="number" step="0.01" value={p.price_gbp_yearly || ""} onChange={(e) => setPlan(i, "price_gbp_yearly", e.target.value)} data-testid={`plan-price-yr-${p.plan_id}`} /></div>
                <div><Label>AI allowance</Label><Input type="number" value={p.ai_allowance} onChange={(e) => setPlan(i, "ai_allowance", e.target.value)} data-testid={`plan-ai-${p.plan_id}`} /></div>
                <div className="flex items-end gap-3 text-xs text-white/60">
                  <label className="flex items-center gap-1"><input type="checkbox" checked={!!p.active} onChange={(e) => setPlan(i, "active", e.target.checked)} /> Active</label>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={!!p.trial_available} onChange={(e) => setPlan(i, "trial_available", e.target.checked)} /> Trial</label>
                </div>
              </div>
              <div className="text-[10px] text-white/30 mt-1">stripe: {p.stripe_price_id ? "synced" : "not synced"}</div>
              <Btn variant="ghost" onClick={() => savePlan(p)} data-testid={`plan-save-${p.plan_id}`} className="mt-2 text-xs">Save</Btn>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h2 className="font-semibold mb-4">AI credit packs</h2>
        <div className="space-y-3" data-testid="pack-editor">
          {packs.map((p, i) => (
            <div key={p.pack_id} className="bg-[#0a0a0a] border border-white/10 rounded p-3" data-testid={`pack-${p.pack_id}`}>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Name</Label><Input value={p.name} onChange={(e) => setPack(i, "name", e.target.value)} data-testid={`pack-name-${p.pack_id}`} /></div>
                <div><Label>Credits</Label><Input type="number" value={p.credits} onChange={(e) => setPack(i, "credits", e.target.value)} data-testid={`pack-credits-${p.pack_id}`} /></div>
                <div><Label>Price £</Label><Input type="number" step="0.01" value={p.price_gbp} onChange={(e) => setPack(i, "price_gbp", e.target.value)} data-testid={`pack-price-${p.pack_id}`} /></div>
              </div>
              <label className="flex items-center gap-1 text-xs text-white/60 mt-2"><input type="checkbox" checked={!!p.active} onChange={(e) => setPack(i, "active", e.target.checked)} /> Active</label>
              <div className="text-[10px] text-white/30 mt-1">stripe: {p.stripe_price_id ? "synced" : "not synced"}</div>
              <Btn variant="ghost" onClick={() => savePack(p)} data-testid={`pack-save-${p.pack_id}`} className="mt-2 text-xs">Save</Btn>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Inner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const sync = async () => { setSyncing(true); try { const r = await platformStripeSync(); toast.success(`Synced ${r.plans.length} plans + ${r.packs.length} packs to Stripe`); } catch (e) { toast.error(e?.response?.data?.detail || "Stripe sync failed"); } finally { setSyncing(false); } };
  return (
    <Shell>
      <div className="flex justify-between items-center">
        <div><div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-1">Platform Console</div><H1>Billing & Plans</H1></div>
        <div className="flex gap-4 text-sm items-center">
          <Link to="/platform" className="text-white/60 hover:text-[#00D26A]">Tenants</Link>
          <Link to="/platform/analytics" className="text-white/60 hover:text-[#00D26A]" data-testid="nav-analytics">Analytics</Link>
          <Btn onClick={sync} disabled={syncing} data-testid="stripe-sync-btn" className="text-xs">{syncing ? "Syncing…" : "Sync to Stripe"}</Btn>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-white/60 hover:text-red-400">Sign out</button>
        </div>
      </div>
      <div className="space-y-6 mt-8">
        <TrialStats />
        <TenantTable />
        <PlanEditor />
      </div>
    </Shell>
  );
}

export default function PlatformBilling() { return <RequirePlatform><Inner /></RequirePlatform>; }
